import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clapperboard, Image as ImageIcon, Mic2, PlayCircle, RefreshCw, ShieldCheck, Sparkles, UserSquare2, Video, Wand2 } from 'lucide-react';
import { toast } from '../ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { VideoCampaignsPanel } from './VideoCampaignsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyle = (status) => {
  if (['ready', 'connected', 'workflow_ready'].includes(status)) return 'border border-primary/20 bg-primary/10 text-primary';
  if (['mapped', 'external_required'].includes(status)) return 'border border-sky-400/20 bg-sky-500/10 text-sky-200';
  return 'border border-amber-400/20 bg-amber-500/10 text-amber-200';
};

const ToolCard = ({ title, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{title}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

export default function ProductionOSHub({ authFetch }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeView, setActiveView] = useState('console');
  const [cloneForm, setCloneForm] = useState({ asset_id: '', voice_name: '', description: '' });
  const [cloneFiles, setCloneFiles] = useState([]);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [renderForm, setRenderForm] = useState({ voice_id: '', text: '', prompt_asset_id: '' });
  const [renderLoading, setRenderLoading] = useState(false);
  const [avatarForm, setAvatarForm] = useState({ asset_id: '', audio_render_id: '', prompt: 'Founder speaking to camera with premium, persuasive and calm authority.' });
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [frameioStructure, setFrameioStructure] = useState(null);
  const [frameioDrafts, setFrameioDrafts] = useState({});
  const [frameioSubfolderForms, setFrameioSubfolderForms] = useState({});
  const [frameioBusyKey, setFrameioBusyKey] = useState(null);
  const [polloForm, setPolloForm] = useState({
    cinematic_ads: { title: 'Spot cinematic Neuralia', prompt: '', model_key: 'pollo-v2-0', aspect_ratio: '9:16' },
    ugc_creator: { title: 'UGC vertical Neuralia', prompt: '', model_key: 'pollo-v2-0', aspect_ratio: '9:16' },
    broll_premium: { title: 'B-roll premium campaña', prompt: '', model_key: 'pollo-v2-0', aspect_ratio: '9:16' },
  });
  const [polloBusy, setPolloBusy] = useState(null);

  const loadData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const res = await authFetch(`${API}/admin/production-os`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo cargar Production OS');
      setData(json);
      setFrameioStructure(json.frameio_structure || null);
      if (showToast) toast.success('Production OS actualizado');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar Production OS');
    } finally {
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  const approvedAssets = useMemo(() => data?.approved_assets || [], [data]);
  const voices = data?.voices || [];
  const catalogVoices = data?.catalog_voices || [];
  const voiceRenders = data?.voice_renders || [];
  const avatarJobs = data?.avatar_jobs || [];
  const premiumStack = data?.premium_stack || [];
  const cinematicGaps = data?.cinematic_gaps || [];
  const referenceInsights = data?.reference_insights || [];
  const actionPlan = data?.action_plan || [];
  const clientOffer = data?.client_offer || [];
  const decision = data?.decision || null;
  const activationStatus = data?.activation_status || {};
  const studioWorkflow = data?.studio_workflow || [];
  const operationalChecklist = data?.operational_checklist || [];
  const frameioRootFolders = frameioStructure?.root_folders || [];
  const frameioSubfolders = frameioStructure?.subfolders || [];
  const youtubeReference = frameioStructure?.youtube_reference || null;
  const publicationChannels = frameioStructure?.publication_channels || [];
  const polloJobs = data?.pollo_jobs || [];

  const selectedAsset = useMemo(() => approvedAssets.find((item) => item.id === cloneForm.asset_id), [approvedAssets, cloneForm.asset_id]);

  const submitVoiceClone = async (e) => {
    e.preventDefault();
    if (!cloneForm.asset_id || !cloneForm.voice_name || cloneFiles.length === 0) return toast.error('Selecciona asset, nombre y muestras de voz');
    try {
      setCloneLoading(true);
      const formData = new FormData();
      formData.append('asset_id', cloneForm.asset_id);
      formData.append('voice_name', cloneForm.voice_name);
      formData.append('description', cloneForm.description);
      cloneFiles.forEach((file) => formData.append('voice_files', file));
      const res = await authFetch(`${API}/admin/production-os/voices/clone`, { method: 'POST', body: formData });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo clonar la voz');
      toast.success('Clon de voz creado');
      setCloneForm({ asset_id: '', voice_name: '', description: '' });
      setCloneFiles([]);
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo clonar la voz');
    } finally {
      setCloneLoading(false);
    }
  };

  const submitVoiceRender = async (e) => {
    e.preventDefault();
    if (!renderForm.voice_id || !renderForm.text.trim()) return toast.error('Selecciona una voz y escribe el texto');
    try {
      setRenderLoading(true);
      const res = await authFetch(`${API}/admin/production-os/voices/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice_id: renderForm.voice_id, text: renderForm.text }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo generar la locución');
      toast.success('Locución generada');
      setRenderForm({ voice_id: '', text: '', prompt_asset_id: '' });
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo generar la locución');
    } finally {
      setRenderLoading(false);
    }
  };

  const submitAvatarJob = async (e) => {
    e.preventDefault();
    if (!avatarForm.asset_id || !avatarForm.audio_render_id || !avatarForm.prompt.trim()) return toast.error('Selecciona asset, audio y prompt');
    try {
      setAvatarLoading(true);
      const res = await authFetch(`${API}/admin/production-os/avatar-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(avatarForm),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo lanzar el avatar');
      toast.success('Job de avatar enviado');
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo lanzar el avatar');
    } finally {
      setAvatarLoading(false);
    }
  };

  const refreshAvatarJob = async (jobId) => {
    try {
      const res = await authFetch(`${API}/admin/production-os/avatar-jobs/${jobId}/refresh`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo refrescar el job');
      toast.success('Estado del avatar actualizado');
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo refrescar el job');
    }
  };

  const connectFrameio = () => { window.location.href = `${API}/auth/frameio/start`; };

  const syncFrameioStructure = async () => {
    try {
      setFrameioBusyKey('sync');
      const res = await authFetch(`${API}/admin/frameio/structure/bootstrap`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo sincronizar Frame.io');
      setFrameioStructure(json.structure || null);
      toast.success('Frame.io sincronizado');
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo sincronizar Frame.io');
    } finally {
      setFrameioBusyKey(null);
    }
  };

  const renameFrameioFolder = async (folderId) => {
    const nextName = (frameioDrafts[folderId] || '').trim();
    if (!nextName) return toast.error('Escribe un nombre válido');
    try {
      setFrameioBusyKey(`rename-${folderId}`);
      const res = await authFetch(`${API}/admin/frameio/folders/${folderId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ folder_id: folderId, name: nextName }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo renombrar la carpeta');
      setFrameioStructure(json.structure || null);
      toast.success('Carpeta renombrada');
    } catch (error) {
      toast.error(error.message || 'No se pudo renombrar la carpeta');
    } finally {
      setFrameioBusyKey(null);
    }
  };

  const createFrameioSubfolder = async (parentFolderId, parentKey) => {
    const nextName = (frameioSubfolderForms[parentFolderId] || '').trim();
    if (!nextName) return toast.error('Escribe el nombre de la subcarpeta');
    try {
      setFrameioBusyKey(`create-${parentFolderId}`);
      const res = await authFetch(`${API}/admin/frameio/folders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parent_folder_id: parentFolderId, name: nextName, parent_key: parentKey }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo crear la subcarpeta');
      setFrameioStructure(json.structure || null);
      setFrameioSubfolderForms((prev) => ({ ...prev, [parentFolderId]: '' }));
      toast.success('Subcarpeta creada');
    } catch (error) {
      toast.error(error.message || 'No se pudo crear la subcarpeta');
    } finally {
      setFrameioBusyKey(null);
    }
  };

  const submitPolloJob = async (jobType) => {
    const draft = polloForm[jobType];
    if (!draft?.title?.trim() || !draft?.prompt?.trim()) return toast.error('Completa título y prompt para Pollo.ai');
    try {
      setPolloBusy(jobType);
      const res = await authFetch(`${API}/admin/production-os/pollo-jobs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ job_type: jobType, ...draft }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo crear el job en Pollo.ai');
      toast.success('Job de Pollo.ai creado');
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo crear el job en Pollo.ai');
    } finally {
      setPolloBusy(null);
    }
  };

  const refreshPolloJob = async (jobId) => {
    try {
      setPolloBusy(jobId);
      const res = await authFetch(`${API}/admin/production-os/pollo-jobs/${jobId}/refresh`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo refrescar el job Pollo.ai');
      toast.success('Job Pollo.ai actualizado');
      await loadData(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo refrescar el job Pollo.ai');
    } finally {
      setPolloBusy(null);
    }
  };

  if (!data) return <div className="glass-card p-8 text-center text-text-muted" data-testid="production-os-loading">Cargando Production OS...</div>;

  return (
    <div className="space-y-6" data-testid="production-os-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="production-os-hero">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Production OS</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · studio console modular</p>
            <h3 className="mt-2 text-3xl font-semibold text-text-heading">Pipeline interno de producción, clonación y exportación audiovisual</h3>
            <p className="mt-3 max-w-3xl text-sm text-text-body">La mejora aquí es convertir un panel muy potente en una consola de estudio más rápida: resumen ejecutivo arriba, vistas separadas y acciones concentradas por tipo de trabajo.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="production-os-diagnosis-chips">
              <span className="insight-chip">Studio console</span>
              <span className="insight-chip">Tool views</span>
              <span className="insight-chip">Frame.io claro</span>
              <span className="insight-chip">Acciones agrupadas</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <ToolCard title="Assets voz" value={data.readiness?.voice_clone_assets || 0} helper="aprobados para clonación" testId="production-os-metric-voice-assets" />
            <ToolCard title="Assets cara" value={data.readiness?.face_clone_assets || 0} helper="listos para avatar" testId="production-os-metric-face-assets" />
            <ToolCard title="Locuciones" value={data.readiness?.voice_renders_created || 0} helper="renders de voz generados" testId="production-os-metric-renders" />
            <ToolCard title="Avatares" value={data.readiness?.avatar_jobs_total || 0} helper="jobs lanzados" testId="production-os-metric-avatars" />
          </div>
        </div>
      </div>

      <div className="glass-card tracing-panel p-6" data-testid="production-os-tabs-shell">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Navigation layer</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Divide la producción en consola, stack, review y ejecución</h4>
          </div>
          <button type="button" onClick={() => loadData(true)} className="btn-secondary" data-testid="production-os-refresh-button">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refrescar
          </button>
        </div>

        <Tabs value={activeView} onValueChange={setActiveView} className="mt-5" data-testid="production-os-tabs-root">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-[20px] border border-border-subtle bg-background/40 p-2">
            <TabsTrigger value="console" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="production-os-tab-console">Console</TabsTrigger>
            <TabsTrigger value="stack" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="production-os-tab-stack">Stack</TabsTrigger>
            <TabsTrigger value="review" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="production-os-tab-review">Review</TabsTrigger>
            <TabsTrigger value="tools" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="production-os-tab-tools">Tools</TabsTrigger>
            <TabsTrigger value="campaigns" className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="production-os-tab-campaigns">Vídeos Campaña</TabsTrigger>
          </TabsList>

          <TabsContent value="campaigns" className="mt-5 space-y-6" data-testid="production-os-view-campaigns">
            <VideoCampaignsPanel authFetch={authFetch} />
          </TabsContent>

          <TabsContent value="console" className="mt-5 space-y-6" data-testid="production-os-view-console">
            {decision && (
              <div className="glass-card p-6" data-testid="production-os-decision-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-primary/80">Decisión estratégica</p>
                <p className="mt-2 text-lg font-semibold text-text-heading">{decision.positioning}</p>
                <p className="mt-2 text-sm text-text-body">Stack elegido: <span className="text-text-heading">{decision.final_stack}</span></p>
              </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
              <div className="glass-card p-6" data-testid="production-os-action-plan-card">
                <div className="flex items-center gap-2 text-text-heading"><RefreshCw className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Qué acciones tomo para llegar ahí</h4></div>
                <div className="mt-4 space-y-3">
                  {actionPlan.map((item, index) => (
                    <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-action-plan-${index}`}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                        <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item.priority}</span>
                      </div>
                      <p className="mt-2 text-sm text-text-body">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="production-os-client-offer-card">
                <div className="flex items-center gap-2 text-text-heading"><ShieldCheck className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Lo que podremos vender con este sistema</h4></div>
                <div className="mt-4 grid gap-3">
                  {clientOffer.map((item, index) => (
                    <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`production-os-client-offer-${index}`}>{item}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
              <div className="glass-card p-6" data-testid="production-os-cinematic-gap-card">
                <div className="flex items-center gap-2 text-text-heading"><Video className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Lo que falta para vernos como líderes</h4></div>
                <div className="mt-4 grid gap-3">
                  {cinematicGaps.map((item, index) => <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`production-os-cinematic-gap-${index}`}>{item}</div>)}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="production-os-reference-insights-card">
                <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Insights del demo de referencia</h4></div>
                <div className="mt-4 space-y-3">
                  {referenceInsights.map((item, index) => (
                    <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-reference-insight-${index}`}>
                      <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                      <p className="mt-2 text-sm text-text-body">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="stack" className="mt-5 space-y-6" data-testid="production-os-view-stack">
            <div className="glass-card p-6" data-testid="production-os-premium-stack-card">
              <div className="flex items-center gap-2 text-text-heading"><Clapperboard className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Stack premium elegido para liderar</h4></div>
              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {premiumStack.map((item) => (
                  <div key={item.key} className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-premium-tool-${item.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.role}</p>
                        <p className="mt-2 text-base font-semibold text-text-heading">{item.name}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] ${statusStyle(item.status)}`}>{item.status}</span>
                    </div>
                    <p className="mt-3 text-sm text-text-body">{item.why}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
              <div className="glass-card p-6" data-testid="production-os-activation-status-card">
                <div className="flex items-center gap-2 text-text-heading"><Mic2 className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Estado de activación real</h4></div>
                <div className="mt-4 grid gap-3">
                  {Object.entries(activationStatus).map(([key, item]) => (
                    <div key={key} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-activation-${key}`}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold capitalize text-text-heading">{key}</p>
                        <span className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] ${statusStyle(item.status)}`}>{item.status}</span>
                      </div>
                      <p className="mt-2 text-sm text-text-body">{item.next_step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="production-os-operational-checklist-card">
                <div className="flex items-center gap-2 text-text-heading"><ShieldCheck className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Checklist para estar operativos</h4></div>
                <div className="mt-4 grid gap-3">
                  {operationalChecklist.map((item, index) => <div key={index} className="rounded-[22px] border border-border-subtle bg-background/55 p-4 text-sm text-text-body" data-testid={`production-os-operational-check-${index}`}>{item}</div>)}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="review" className="mt-5 space-y-6" data-testid="production-os-view-review">
            <div className="glass-card p-6" data-testid="production-os-frameio-structure-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Frame.io Operations</p>
                  <h4 className="mt-2 text-xl font-semibold text-text-heading">Estructura operativa real del proyecto</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary" onClick={connectFrameio} data-testid="production-os-frameio-connect-button">Conectar Frame.io OAuth</button>
                  <button type="button" className="btn-secondary" onClick={syncFrameioStructure} disabled={frameioBusyKey === 'sync'} data-testid="production-os-frameio-sync-button"><RefreshCw className={`h-4 w-4 ${frameioBusyKey === 'sync' ? 'animate-spin' : ''}`} /> Sincronizar</button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
                <div className="space-y-4" data-testid="production-os-frameio-root-folders">
                  {frameioRootFolders.map((folder) => {
                    const linkedSubfolders = frameioSubfolders.filter((item) => item.parent_folder_id === folder.id);
                    return (
                      <div key={folder.id} className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid={`production-os-frameio-root-folder-${folder.id}`}>
                        <p className="text-lg font-semibold text-text-heading">{folder.name}</p>
                        <p className="mt-1 text-xs text-text-muted">Folder ID: {folder.id}</p>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <input className="input-kinesthetic min-w-[220px] flex-1" value={frameioDrafts[folder.id] ?? folder.name} onChange={(e) => setFrameioDrafts((prev) => ({ ...prev, [folder.id]: e.target.value }))} data-testid={`production-os-frameio-rename-input-${folder.id}`} />
                          <button type="button" className="btn-secondary" onClick={() => renameFrameioFolder(folder.id)} disabled={frameioBusyKey === `rename-${folder.id}`} data-testid={`production-os-frameio-rename-button-${folder.id}`}>Renombrar</button>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <input className="input-kinesthetic min-w-[220px] flex-1" placeholder="Nueva subcarpeta manual" value={frameioSubfolderForms[folder.id] || ''} onChange={(e) => setFrameioSubfolderForms((prev) => ({ ...prev, [folder.id]: e.target.value }))} data-testid={`production-os-frameio-subfolder-input-${folder.id}`} />
                          <button type="button" className="btn-primary" onClick={() => createFrameioSubfolder(folder.id, folder.name)} disabled={frameioBusyKey === `create-${folder.id}`} data-testid={`production-os-frameio-subfolder-button-${folder.id}`}>Crear subcarpeta</button>
                        </div>
                        <div className="mt-4 grid gap-2 md:grid-cols-2">
                          {linkedSubfolders.length === 0 ? <p className="text-xs text-text-muted">Sin subcarpetas manuales todavía.</p> : linkedSubfolders.map((item) => <div key={item.id} className="rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`production-os-frameio-subfolder-${item.id}`}><p className="text-sm font-semibold text-text-heading">{item.name}</p><p className="mt-1 text-xs text-text-muted">{item.parent_key || 'subfolder'} · {item.id}</p></div>)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-4">
                  <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="production-os-frameio-summary-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Resumen operativo</p>
                    <div className="mt-3 space-y-2 text-sm text-text-body">
                      <p>Proyecto: <strong>{frameioStructure?.project_path || '/project/sin-definir'}</strong></p>
                      <p>Root folder ID: <strong>{frameioStructure?.root_folder_id || 'pendiente'}</strong></p>
                      <p>Modo sync: <strong>{frameioStructure?.sync_mode || 'pending'}</strong></p>
                      <p>Estado: <strong>{frameioStructure?.sync_status || 'pending'}</strong></p>
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="production-os-youtube-reference-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Referencia editorial YouTube</p>
                    <p className="mt-2 text-base font-semibold text-text-heading">{youtubeReference?.channel_name || 'Juan Carlos Coach en PNL, Estrategia Comercial IA'}</p>
                    <a href={youtubeReference?.channel_url || 'https://www.youtube.com/@juancarlosvargasvazquez1277'} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-sm font-semibold text-primary underline-offset-4 hover:underline" data-testid="production-os-youtube-reference-link">Abrir canal de referencia</a>
                    <p className="mt-3 text-sm text-text-body">{youtubeReference?.summary || 'Canal registrado como referencia editorial para tono, enfoque y distribución futura de contenido.'}</p>
                  </div>

                  <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="production-os-publication-channels-card">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Canales principales de publicación</p>
                    <div className="mt-3 space-y-3">
                      {publicationChannels.map((channel) => (
                        <div key={channel.channel_key} className="rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-publication-channel-${channel.channel_key}`}>
                          <p className="text-sm font-semibold text-text-heading">{channel.channel_name}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.16em] text-primary">{channel.role}</p>
                          <a href={channel.channel_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-xs font-semibold text-primary underline-offset-4 hover:underline" data-testid={`production-os-publication-channel-link-${channel.channel_key}`}>Abrir canal</a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6" data-testid="production-os-studio-workflow-card">
              <div className="flex items-center gap-2 text-text-heading"><Video className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Workflow premium de estudio para clientes</h4></div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {studioWorkflow.map((step) => <div key={step.step} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-studio-step-${step.step}`}><p className="text-sm font-semibold text-text-heading">{step.step}. {step.label}</p><p className="mt-2 text-sm text-text-body">{step.description}</p></div>)}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tools" className="mt-5 space-y-6" data-testid="production-os-view-tools">
            <div className="grid gap-6 xl:grid-cols-3">
              <form onSubmit={submitVoiceClone} className="glass-card p-6 space-y-4" data-testid="production-os-clone-form">
                <div className="flex items-center gap-2 text-text-heading"><Mic2 className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">1. Clon de voz</h4></div>
                <select className="input-kinesthetic" value={cloneForm.asset_id} onChange={(e) => setCloneForm((prev) => ({ ...prev, asset_id: e.target.value }))} data-testid="production-os-clone-asset-select"><option value="">Selecciona asset autorizado</option>{approvedAssets.filter((asset) => asset.allow_voice_clone).map((asset) => <option key={asset.id} value={asset.id}>{asset.title}</option>)}</select>
                <input className="input-kinesthetic" value={cloneForm.voice_name} onChange={(e) => setCloneForm((prev) => ({ ...prev, voice_name: e.target.value }))} placeholder="Nombre interno de la voz" data-testid="production-os-clone-voice-name" />
                <textarea className="input-kinesthetic min-h-[110px] resize-none" value={cloneForm.description} onChange={(e) => setCloneForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Descripción breve" data-testid="production-os-clone-description" />
                <input type="file" multiple accept="audio/*" className="input-kinesthetic" onChange={(e) => setCloneFiles(Array.from(e.target.files || []))} data-testid="production-os-clone-files" />
                {selectedAsset && <p className="text-xs text-text-muted">Asset base: <span className="text-text-heading">{selectedAsset.title}</span></p>}
                <button type="submit" className="btn-primary w-full" disabled={cloneLoading} data-testid="production-os-clone-submit">{cloneLoading ? 'Clonando…' : 'Crear clon de voz'}</button>
              </form>

              <form onSubmit={submitVoiceRender} className="glass-card p-6 space-y-4" data-testid="production-os-render-form">
                <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">2. Generar locución</h4></div>
                <select className="input-kinesthetic" value={renderForm.voice_id} onChange={(e) => setRenderForm((prev) => ({ ...prev, voice_id: e.target.value }))} data-testid="production-os-render-voice-select"><option value="">Selecciona clon de voz</option>{voices.map((voice) => <option key={voice.id} value={voice.id}>{voice.voice_name} · clon</option>)}{catalogVoices.map((voice) => <option key={voice.provider_voice_id} value={`catalog:${voice.provider_voice_id}`}>{voice.voice_name} · base ElevenLabs</option>)}</select>
                <textarea className="input-kinesthetic min-h-[180px] resize-none" value={renderForm.text} onChange={(e) => setRenderForm((prev) => ({ ...prev, text: e.target.value }))} placeholder="Texto del vídeo / CTA / guion" data-testid="production-os-render-text" />
                <button type="submit" className="btn-primary w-full" disabled={renderLoading} data-testid="production-os-render-submit">{renderLoading ? 'Generando…' : 'Generar audio con ElevenLabs'}</button>
              </form>

              <form onSubmit={submitAvatarJob} className="glass-card p-6 space-y-4" data-testid="production-os-avatar-form">
                <div className="flex items-center gap-2 text-text-heading"><UserSquare2 className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">3. Avatar / vídeo</h4></div>
                <select className="input-kinesthetic" value={avatarForm.asset_id} onChange={(e) => setAvatarForm((prev) => ({ ...prev, asset_id: e.target.value }))} data-testid="production-os-avatar-asset-select"><option value="">Selecciona asset visual</option>{approvedAssets.filter((asset) => asset.allow_face_clone).map((asset) => <option key={asset.id} value={asset.id}>{asset.title}</option>)}</select>
                <select className="input-kinesthetic" value={avatarForm.audio_render_id} onChange={(e) => setAvatarForm((prev) => ({ ...prev, audio_render_id: e.target.value }))} data-testid="production-os-avatar-audio-select"><option value="">Selecciona locución</option>{voiceRenders.map((render) => <option key={render.id} value={render.id}>{render.voice_name}</option>)}</select>
                <textarea className="input-kinesthetic min-h-[140px] resize-none" value={avatarForm.prompt} onChange={(e) => setAvatarForm((prev) => ({ ...prev, prompt: e.target.value }))} placeholder="Prompt visual del avatar" data-testid="production-os-avatar-prompt" />
                <button type="submit" className="btn-primary w-full" disabled={avatarLoading} data-testid="production-os-avatar-submit">{avatarLoading ? 'Enviando…' : 'Lanzar job de avatar'}</button>
              </form>
            </div>

            <div className="grid gap-6 xl:grid-cols-3">
              {[
                ['cinematic_ads', 'Vídeos publicitarios / cinematográficos', 'Spots de autoridad, visión y venta con look premium.'],
                ['ugc_creator', 'UGC / creador hablando a cámara', 'Piezas verticales para TikTok, Reels y Shorts.'],
                ['broll_premium', 'B-roll / escenas premium', 'Escenas de apoyo y textura visual para campañas.'],
              ].map(([jobType, label, helper]) => (
                <div key={jobType} className="glass-card p-6 space-y-4" data-testid={`production-os-pollo-form-${jobType}`}>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Pollo.ai Video Studio</p>
                    <h4 className="mt-2 text-lg font-semibold text-text-heading">{label}</h4>
                    <p className="mt-2 text-sm text-text-body">{helper}</p>
                  </div>
                  <input className="input-kinesthetic" value={polloForm[jobType].title} onChange={(e) => setPolloForm((prev) => ({ ...prev, [jobType]: { ...prev[jobType], title: e.target.value } }))} placeholder="Título interno" data-testid={`production-os-pollo-title-${jobType}`} />
                  <textarea className="input-kinesthetic min-h-[160px] resize-none" value={polloForm[jobType].prompt} onChange={(e) => setPolloForm((prev) => ({ ...prev, [jobType]: { ...prev[jobType], prompt: e.target.value } }))} placeholder="Prompt profesional para Pollo.ai" data-testid={`production-os-pollo-prompt-${jobType}`} />
                  <button type="button" className="btn-primary w-full" onClick={() => submitPolloJob(jobType)} disabled={polloBusy === jobType} data-testid={`production-os-pollo-submit-${jobType}`}>{polloBusy === jobType ? 'Creando…' : 'Crear job en Pollo.ai'}</button>
                </div>
              ))}
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <div className="glass-card p-6" data-testid="production-os-renders-list-card">
                <div className="flex items-center gap-2 text-text-heading"><PlayCircle className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Locuciones generadas</h4></div>
                <div className="mt-4 space-y-3">
                  {voiceRenders.length === 0 && <p className="text-sm text-text-muted">Aún no hay locuciones generadas.</p>}
                  {voiceRenders.map((render) => <div key={render.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-render-${render.id}`}><p className="text-sm font-semibold text-text-heading">{render.voice_name}</p><p className="mt-1 text-xs text-text-muted line-clamp-2">{render.text}</p>{render.audio?.download_url && <audio className="mt-3 w-full" controls src={render.audio.download_url} data-testid={`production-os-render-audio-${render.id}`} />}</div>)}
                </div>
              </div>

              <div className="glass-card p-6" data-testid="production-os-avatar-jobs-card">
                <div className="flex items-center gap-2 text-text-heading"><Clapperboard className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Jobs de avatar</h4></div>
                <div className="mt-4 space-y-4">
                  {avatarJobs.length === 0 && <p className="text-sm text-text-muted">Aún no hay jobs de avatar lanzados.</p>}
                  {avatarJobs.map((job) => <div key={job.id} className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`production-os-avatar-job-${job.id}`}><div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-sm font-semibold text-text-heading">{job.asset_title}</p><p className="mt-1 text-xs text-text-muted">{job.provider_model}</p></div><button type="button" onClick={() => refreshAvatarJob(job.id)} className="btn-secondary" data-testid={`production-os-avatar-refresh-${job.id}`}><RefreshCw className="h-4 w-4" /> Refrescar</button></div><p className="mt-3 text-xs text-text-muted">Estado: <span className="text-text-heading">{job.status}</span></p>{job.result?.video_url && <video className="mt-4 w-full rounded-2xl border border-border-subtle" controls src={job.result.video_url} data-testid={`production-os-avatar-video-${job.id}`} />}</div>)}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}